import {
  apiContract,
  type ApiContract,
  type ApiContractKey,
  type ApiAuthMode,
} from '@bonboca/shared-schemas';
import { apiErrorBodySchema, apiErrorCodeSchema } from '@bonboca/shared-schemas';
import type { z } from 'zod';

import { ApiError, NotImplementedError, type ApiClientErrorCode } from './errors';
import { buildQueryString, serializeQueryCamelToKebab } from './queryString';

const GUEST_ORDER_TOKEN_HEADER = 'x-guest-order-token';

export interface CreateApiClientOptions {
  readonly baseUrl: string;
  readonly getAccessToken?: () => Promise<string | null | undefined>;
  readonly getGuestOrderToken?: (orderId: string) => string | undefined;
}

export type ApiCallInput<K extends ApiContractKey> = ApiCallInputForEntry<ApiContract[K]>;

type ApiCallInputForEntry<E> = E extends { params: infer P extends z.ZodTypeAny }
  ? BaseCallInput<E> & { params: z.input<P> }
  : BaseCallInput<E>;

/** Client `query`: required when a dedicated client schema exists; optional for wire-only query (defaults apply server-side). */
type BaseCallInput<E> = (E extends { query: infer Q extends z.ZodTypeAny }
  ? { query: z.input<Q> }
  : E extends { wireQuery: infer W extends z.ZodTypeAny }
    ? { query?: z.input<W> }
    : object) &
  (E extends { request: infer R extends z.ZodTypeAny } ? { body: z.input<R> } : object) &
  (E extends { auth: 'cognito-or-guest' } ? { guestOrderId?: string } : object) & {
    /** Sent on `POST /v1/orders` as `Idempotency-Key` for safe retries. */
    idempotencyKey?: string;
  };

export type ApiCallOutput<K extends ApiContractKey> = ApiContract[K] extends {
  response: z.ZodTypeAny;
}
  ? z.output<ApiContract[K]['response']>
  : never;

function jsonReplacer(_key: string, value: unknown): unknown {
  if (typeof value === 'bigint') return value.toString();
  return value;
}

function buildUrl(
  baseUrl: string,
  pathTemplate: string,
  params: Record<string, string> | undefined,
): string {
  let path = pathTemplate;
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      path = path.replace(`:${name}`, encodeURIComponent(value));
    }
  }
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${normalizedBase}${path}`;
}

async function executeEndpoint<K extends ApiContractKey>(
  key: K,
  entry: ApiContract[K],
  rawInput: unknown,
  opts: CreateApiClientOptions & { signal?: AbortSignal },
): Promise<unknown> {
  const input = (rawInput ?? {}) as Record<string, unknown>;
  const method = entry.method;
  const pathTemplate = entry.path;
  let pathParams: Record<string, string> | undefined;
  if ('params' in entry && entry.params !== undefined) {
    const parsed = (entry.params as z.ZodTypeAny).parse(input['params']);
    pathParams = Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
    );
  }

  const queryRecord: Record<string, string | undefined> = {};
  if (input['query'] !== undefined) {
    if ('serializeQuery' in entry && typeof entry.serializeQuery === 'function') {
      Object.assign(
        queryRecord,
        (
          entry as { serializeQuery: (q: unknown) => Record<string, string | undefined> }
        ).serializeQuery(input['query']),
      );
    } else if ('query' in entry && entry.query !== undefined) {
      Object.assign(
        queryRecord,
        serializeQueryCamelToKebab(
          input['query'] as Record<
            string,
            string | number | boolean | undefined | null | readonly string[]
          >,
        ),
      );
    } else if ('wireQuery' in entry && entry.wireQuery !== undefined) {
      const q = input['query'] as Record<string, unknown>;
      for (const [k, v] of Object.entries(q)) {
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) {
          if (v.length > 0) queryRecord[k] = v.join(',');
          continue;
        }
        queryRecord[k] = String(v);
      }
    }
  }

  const url = `${buildUrl(opts.baseUrl, pathTemplate, pathParams)}${buildQueryString(
    queryRecord as Record<string, string | number | boolean | undefined | null>,
  )}`;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  const auth = entry.auth as ApiAuthMode;
  if (auth === 'cognito' || auth === 'cognito-or-guest') {
    const token = opts.getAccessToken ? await opts.getAccessToken() : undefined;
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  if (key === 'placeOrder') {
    const idempotencyKey = input['idempotencyKey'] as string | undefined;
    if (idempotencyKey !== undefined && idempotencyKey.trim().length > 0) {
      headers['Idempotency-Key'] = idempotencyKey.trim();
    }
  }

  if (auth === 'cognito-or-guest' && opts.getGuestOrderToken) {
    const guestOrderId = input['guestOrderId'] as string | undefined;
    const orderIdFromParams = pathParams?.['id'];
    const orderId = guestOrderId ?? orderIdFromParams;
    if (orderId) {
      const guestToken = opts.getGuestOrderToken(orderId);
      if (guestToken) headers[GUEST_ORDER_TOKEN_HEADER] = guestToken;
    }
  }

  let body: string | undefined;
  if ('request' in entry && entry.request !== undefined && input['body'] !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(input['body'], jsonReplacer);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body,
      signal: opts.signal,
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', `Network error talking to ${url}`);
  }

  if (response.ok && response.status === 204) {
    return (entry.response as z.ZodTypeAny).parse(undefined);
  }

  if (response.status === 302) {
    return { kind: 'redirect', location: response.headers.get('Location') ?? '' };
  }

  const text = await response.text();
  let json: unknown;
  try {
    json = text.length === 0 ? undefined : JSON.parse(text);
  } catch {
    if (!response.ok) {
      throw new ApiError(response.status, undefined, `HTTP ${response.status}`);
    }
    throw new ApiError(response.status, undefined, `Invalid JSON response (${response.status})`);
  }

  if (!response.ok) {
    const parsed = apiErrorBodySchema.safeParse(json);
    const code = parsed.success ? parsed.data.code : undefined;
    const message =
      parsed.success && parsed.data.message ? parsed.data.message : `HTTP ${response.status}`;
    if (response.status === 501 || code === 'NOT_IMPLEMENTED') {
      throw new NotImplementedError(message);
    }
    const codeParse =
      code !== undefined ? apiErrorCodeSchema.safeParse(code) : { success: false as const };
    const typedCode: ApiClientErrorCode | undefined = codeParse.success
      ? codeParse.data
      : undefined;
    throw new ApiError(response.status, typedCode, message);
  }

  return (entry.response as z.ZodTypeAny).parse(json);
}

export type ApiClient = {
  [K in ApiContractKey]: (
    input: ApiCallInput<K>,
    opts?: { signal?: AbortSignal },
  ) => Promise<ApiCallOutput<K>>;
};

export type ApiClientInstance = ApiClient & { readonly baseUrl: string };

export function createApiClient(options: CreateApiClientOptions): ApiClientInstance {
  const client = {} as Record<string, unknown>;
  for (const key of Object.keys(apiContract) as ApiContractKey[]) {
    client[key] = (input: unknown, callOpts?: { signal?: AbortSignal }) =>
      executeEndpoint(key, apiContract[key], input, { ...options, signal: callOpts?.signal });
  }
  Object.defineProperty(client, 'baseUrl', { value: options.baseUrl, enumerable: true });
  return client as ApiClientInstance;
}

export { apiContract } from '@bonboca/shared-schemas';
